import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import type { IRootStore } from '../stores';
import type { IModel } from '../stores/model';
import type { IFSM } from '../stores/model/metamodel/fsm';

// `MobXProviderContext` is typed `Record<string, any>` by mobx-react; the
// `<Provider store={...}>` exposes the RootStore under the `store` key.
type ProviderValue = { store: IRootStore };

/**
 * Custom hook that returns the currently visible FSM.
 *
 * It fetches the information about the visible FSM from the visibility UI
 * store.
 */
export function useVisibleFSM(model: IModel): IFSM | undefined {
  const {
    store: {
      ui: { visible },
    },
  } = useContext(MobXProviderContext) as ProviderValue;
  if (visible.fsm != null) {
    return model.metamodel.getFSMById(visible.fsm);
  }
}

/**
 * Custom hook that returns whether it is possible to calculate an FSM for the
 * object type of the given FSM.
 */
export function useCanCalculateFSM(fsm: IFSM | null | undefined): boolean {
  return fsm != null && fsm.objectType.fsms.length > 1;
}

/**
 * Custom hook that returns the currently visible FSM and a function to change
 * it.
 *
 * @return An object with:
 * - fsm: the currently visible FSM
 * - showFSM: a function to change the visible FSM.
 */
export function useUpdateVisibleFSM(model: IModel): {
  fsm: IFSM | undefined;
  showFSM: (fsm?: string) => void;
} {
  const {
    store: {
      ui: { visible },
    },
  } = useContext(MobXProviderContext) as ProviderValue;
  let fsm;
  if (visible.fsm != null) {
    fsm = model.metamodel.getFSMById(visible.fsm);
  }
  return {
    fsm,
    showFSM: visible.showFSM,
  };
}
